import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Mail, Edit, Save, Eye, Loader2, RotateCcw, Globe, FileText } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";

// Template type descriptions
const TEMPLATE_TYPES = {
  business_registration: { label: "Iscrizione Azienda", icon: "🏢" },
  admin_registration: { label: "Iscrizione Admin", icon: "👤" },
  booking_confirmation: { label: "Conferma Prenotazione", icon: "✅" },
  booking_modification: { label: "Modifica Prenotazione", icon: "✏️" },
  payment_confirmation: { label: "Conferma Pagamento", icon: "💳" }
};

const LANGUAGE_FLAGS = {
  it: "🇮🇹",
  en: "🇬🇧",
  de: "🇩🇪",
  es: "🇪🇸"
};

const EmailTemplatesManager = ({ role = "superadmin", businessId = null }) => {
  const { t } = useTranslation();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedType, setSelectedType] = useState("booking_confirmation");
  const [selectedLanguage, setSelectedLanguage] = useState("it");
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewContent, setPreviewContent] = useState({ subject: "", body_html: "" });
  const [saving, setSaving] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);

  // Determine available types based on role
  const availableTypes = role === "business" 
    ? ["booking_confirmation", "booking_modification", "payment_confirmation"]
    : Object.keys(TEMPLATE_TYPES);

  useEffect(() => {
    fetchTemplates();
  }, [role, businessId]);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      let endpoint = `${API}/superadmin/email-templates`;
      
      if (role === "admin" && businessId) {
        endpoint = `${API}/admin/businesses/${businessId}/email-templates`;
      } else if (role === "business") {
        endpoint = `${API}/my-business/email-templates`;
      }
      
      const res = await axios.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(res.data);
    } catch (error) {
      console.error("Error fetching templates:", error);
      toast.error("Errore nel caricamento dei template");
    } finally {
      setLoading(false);
    }
  };

  const getCurrentTemplate = () => {
    return templates.find(
      t => t.template_type === selectedType && t.language === selectedLanguage
    );
  };

  const handleEdit = () => {
    const template = getCurrentTemplate();
    if (template) {
      setEditingTemplate({
        ...template,
        subject: template.subject,
        body_html: template.body_html
      });
      setEditDialogOpen(true);
    }
  };

  const handleSave = async () => {
    if (!editingTemplate) return;
    
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      let endpoint = `${API}/superadmin/email-templates`;
      
      if (role === "admin" && businessId) {
        endpoint = `${API}/admin/businesses/${businessId}/email-templates`;
      } else if (role === "business") {
        endpoint = `${API}/my-business/email-templates`;
      }
      
      await axios.post(endpoint, {
        template_type: selectedType,
        language: selectedLanguage,
        subject: editingTemplate.subject,
        body_html: editingTemplate.body_html,
        is_active: true
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Template salvato con successo");
      setEditDialogOpen(false);
      fetchTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const handleResetToDefault = async () => {
    const template = getCurrentTemplate();
    if (!template || template.source === "default" || template.is_default) {
      toast.info("Questo template usa già il default");
      return;
    }
    
    if (!confirm("Vuoi ripristinare il template di default? Le tue modifiche verranno perse.")) return;
    
    try {
      const token = localStorage.getItem("token");
      let endpoint = "";
      
      if (role === "superadmin") {
        endpoint = `${API}/superadmin/email-templates/${template.template_id}`;
      } else if (role === "business") {
        endpoint = `${API}/my-business/email-templates/${template.template_id}`;
      }
      
      if (endpoint) {
        await axios.delete(endpoint, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Template ripristinato al default");
        fetchTemplates();
      }
    } catch (error) {
      toast.error("Errore nel ripristino del template");
    }
  };

  const handlePreview = async () => {
    const template = getCurrentTemplate();
    if (!template) return;
    
    setLoadingPreview(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(`${API}/email-templates/preview`, {
        subject: template.subject,
        body_html: template.body_html,
        template_type: selectedType
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setPreviewContent(res.data);
      setPreviewDialogOpen(true);
    } catch (error) {
      toast.error("Errore nel caricamento dell'anteprima");
    } finally {
      setLoadingPreview(false);
    }
  };

  const currentTemplate = getCurrentTemplate();

  if (loading) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
          <p className="mt-4 text-slate-500">Caricamento template...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="p-2 bg-purple-100 rounded-lg">
            <Mail className="h-5 w-5 text-purple-600" />
          </div>
          <div>
            <CardTitle>Template Email</CardTitle>
            <CardDescription>
              Personalizza le email inviate automaticamente dal sistema
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Type and Language Selection */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Tipo di Email</Label>
            <Select value={selectedType} onValueChange={setSelectedType}>
              <SelectTrigger data-testid="select-template-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {availableTypes.map(type => (
                  <SelectItem key={type} value={type}>
                    <span className="flex items-center gap-2">
                      <span>{TEMPLATE_TYPES[type]?.icon}</span>
                      <span>{TEMPLATE_TYPES[type]?.label}</span>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <Label>Lingua</Label>
            <Select value={selectedLanguage} onValueChange={setSelectedLanguage}>
              <SelectTrigger data-testid="select-template-language">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(LANGUAGE_FLAGS).map(([code, flag]) => (
                  <SelectItem key={code} value={code}>
                    <span className="flex items-center gap-2">
                      <span>{flag}</span>
                      <span>{code.toUpperCase()}</span>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Current Template Info */}
        {currentTemplate && (
          <div className="p-4 border rounded-lg bg-slate-50">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-slate-500" />
                <span className="font-medium">Oggetto:</span>
              </div>
              <Badge 
                variant="outline" 
                className={
                  currentTemplate.source === "custom" || currentTemplate.business_id 
                    ? "bg-green-50 text-green-700 border-green-200"
                    : currentTemplate.source === "global"
                    ? "bg-blue-50 text-blue-700 border-blue-200"
                    : "bg-slate-50 text-slate-600"
                }
              >
                {currentTemplate.source === "custom" || currentTemplate.business_id 
                  ? "Personalizzato" 
                  : currentTemplate.source === "global" 
                  ? "Globale"
                  : "Default"}
              </Badge>
            </div>
            <p className="text-sm text-slate-700 mb-4 font-mono bg-white p-2 rounded border">
              {currentTemplate.subject}
            </p>
            
            <div className="flex items-center gap-2 mb-2">
              <Globe className="h-4 w-4 text-slate-500" />
              <span className="font-medium">Anteprima contenuto:</span>
            </div>
            <div 
              className="text-sm text-slate-600 max-h-40 overflow-y-auto bg-white p-3 rounded border"
              dangerouslySetInnerHTML={{ 
                __html: currentTemplate.body_html?.substring(0, 500) + "..." 
              }}
            />
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-wrap gap-2">
          <Button onClick={handleEdit} data-testid="edit-template-btn">
            <Edit className="h-4 w-4 mr-2" />
            Modifica Template
          </Button>
          <Button variant="outline" onClick={handlePreview} disabled={loadingPreview}>
            {loadingPreview ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Eye className="h-4 w-4 mr-2" />
            )}
            Anteprima
          </Button>
          {currentTemplate && (currentTemplate.source !== "default" && !currentTemplate.is_default) && (
            <Button variant="ghost" onClick={handleResetToDefault}>
              <RotateCcw className="h-4 w-4 mr-2" />
              Ripristina Default
            </Button>
          )}
        </div>

        {/* Variable Reference */}
        <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
          <h4 className="font-medium text-blue-800 mb-2">Variabili disponibili:</h4>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs font-mono text-blue-700">
            {selectedType === "business_registration" || selectedType === "admin_registration" ? (
              <>
                <span>{"{{platform_name}}"}</span>
                <span>{"{{user_name}}"}</span>
                <span>{"{{user_email}}"}</span>
                <span>{"{{temp_password}}"}</span>
                <span>{"{{login_url}}"}</span>
                <span>{"{{reset_password_url}}"}</span>
                {selectedType === "business_registration" && <span>{"{{business_name}}"}</span>}
              </>
            ) : (
              <>
                <span>{"{{customer_name}}"}</span>
                <span>{"{{business_name}}"}</span>
                <span>{"{{booking_code}}"}</span>
                <span>{"{{services_list}}"}</span>
                <span>{"{{booking_date}}"}</span>
                <span>{"{{booking_time}}"}</span>
                <span>{"{{total_duration}}"}</span>
                <span>{"{{total_price}}"}</span>
                <span>{"{{staff_info}}"}</span>
                <span>{"{{business_email}}"}</span>
                <span>{"{{business_phone}}"}</span>
                <span>{"{{business_address}}"}</span>
                {selectedType === "payment_confirmation" && (
                  <>
                    <span>{"{{transaction_id}}"}</span>
                    <span>{"{{payment_date}}"}</span>
                    <span>{"{{payment_amount}}"}</span>
                    <span>{"{{payment_method}}"}</span>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </CardContent>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Modifica Template - {TEMPLATE_TYPES[selectedType]?.label} ({selectedLanguage.toUpperCase()})
            </DialogTitle>
            <DialogDescription>
              Personalizza oggetto e contenuto della email
            </DialogDescription>
          </DialogHeader>
          
          {editingTemplate && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="subject">Oggetto Email</Label>
                <Input
                  id="subject"
                  value={editingTemplate.subject}
                  onChange={(e) => setEditingTemplate({
                    ...editingTemplate,
                    subject: e.target.value
                  })}
                  placeholder="Oggetto della email..."
                  data-testid="template-subject-input"
                />
              </div>
              
              <div>
                <Label htmlFor="body">Contenuto HTML</Label>
                <Textarea
                  id="body"
                  value={editingTemplate.body_html}
                  onChange={(e) => setEditingTemplate({
                    ...editingTemplate,
                    body_html: e.target.value
                  })}
                  placeholder="<div>Contenuto HTML...</div>"
                  className="min-h-[300px] font-mono text-sm"
                  data-testid="template-body-input"
                />
              </div>
              
              <div className="p-3 bg-amber-50 rounded border border-amber-200">
                <p className="text-sm text-amber-800">
                  <strong>Suggerimento:</strong> Usa le variabili tra doppie parentesi graffe, es: {"{{customer_name}}"}
                </p>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Annulla
            </Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-template-btn">
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Salvataggio...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Salva Template
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={previewDialogOpen} onOpenChange={setPreviewDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Anteprima Email</DialogTitle>
            <DialogDescription>
              Come apparirà la email con dati di esempio
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="p-3 bg-slate-100 rounded">
              <Label className="text-xs text-slate-500">Oggetto:</Label>
              <p className="font-medium">{previewContent.subject}</p>
            </div>
            
            <div className="border rounded-lg p-4 bg-white">
              <div 
                dangerouslySetInnerHTML={{ __html: previewContent.body_html }}
                className="prose prose-sm max-w-none"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button onClick={() => setPreviewDialogOpen(false)}>
              Chiudi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default EmailTemplatesManager;
